import {openAddParameterDialog} from '@/modules/patients/components/patient-details/therapy/dialogs/util'
import {
  Box,
  Button,
  ButtonGroup,
  DialogBody,
  DialogFooter,
  DialogHeader,
  RadioCard,
  Text,
  VStack,
} from '@chakra-ui/react'
import {PatientProtocol} from '@cypro/engine/queries'
import {dialog} from '@cypro/ui/components'
import {useTranslate} from '@tolgee/react'
import {useState} from 'react'
import {LuPillBottle, LuQuote} from 'react-icons/lu'
import {AddInstructionNoteDialog} from './add-instruction-note'

function onClose() {
  dialog.close('add-therapy-entry', true)
}

function isProtocolActive(patientProtocol: PatientProtocol) {
  // endDate is only set when a protocol is stopped
  if (!patientProtocol.endDate) return true
  const now = Date.now()
  const endOfEndDate = new Date(patientProtocol.endDate)
  endOfEndDate.setHours(23, 59, 59, 999)
  return now <= endOfEndDate.getTime()
}

export function AddEntryDialog({
  patientProtocols,
  patientId,
}: {
  patientProtocols: PatientProtocol[]
  patientId: string
}) {
  const {t} = useTranslate('common')

  const [value, setValue] = useState<string | null>('parameter')

  const activePatientProtocols = patientProtocols.filter(isProtocolActive)

  function onClickParameter() {
    dialog.close('add-therapy-entry', true)
    openAddParameterDialog({patientProtocols: activePatientProtocols, patientId})
  }

  function onClickNote() {
    dialog.close('add-therapy-entry', true)
    dialog.open('add-instruction-note', {
      children: <AddInstructionNoteDialog patientProtocols={activePatientProtocols} patientId={patientId} />,
    })
  }

  return (
    <>
      <DialogHeader>
        <Box display={'flex'} flexDirection={'column'} gap="1">
          <Text fontWeight={'semibold'} fontSize={'lg'}>
            {t('therapy.add_entry.title')}
          </Text>
          <Text fontWeight={'normal'} fontSize={'md'} color="fg.muted">
            {t('protocol.add_entry.description')}
          </Text>
        </Box>
      </DialogHeader>

      <DialogBody minHeight={'none'} maxWidth={'100%'}>
        <RadioCard.Root value={value} onValueChange={e => setValue(e.value)}>
          <VStack align="stretch">
            <RadioCard.Item key={0} value="parameter">
              <RadioCard.ItemHiddenInput />
              <RadioCard.ItemControl>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  justifyContent={'flex-start'}
                  gap={2}
                  padding={2}
                  width={'100%'}
                >
                  <LuPillBottle size={24} />
                  <Box>
                    <Text fontWeight={'medium'} fontSize={'sm'}>
                      {t('protocol.add_entry.parameter.title')}
                    </Text>
                    <Text fontWeight={'normal'} fontSize={'sm'} color="fg.muted">
                      {t('protocol.add_entry.parameter.description')}
                    </Text>
                  </Box>
                </Box>
                <RadioCard.ItemIndicator />
              </RadioCard.ItemControl>
            </RadioCard.Item>
            <RadioCard.Item key={1} value="note">
              <RadioCard.ItemHiddenInput />
              <RadioCard.ItemControl>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  justifyContent={'flex-start'}
                  gap={2}
                  padding={2}
                  width={'100%'}
                >
                  <LuQuote size={24} />
                  <Box>
                    <Text fontWeight={'medium'} fontSize={'sm'}>
                      {t('protocol.add_entry.note.title')}
                    </Text>
                    <Text fontWeight={'normal'} fontSize={'sm'} color="fg.muted">
                      {t('protocol.add_entry.note.description')}
                    </Text>
                  </Box>
                </Box>
                <RadioCard.ItemIndicator />
              </RadioCard.ItemControl>
            </RadioCard.Item>
          </VStack>
        </RadioCard.Root>
      </DialogBody>
      <DialogFooter>
        <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
          <Button variant={'outline'} colorPalette={'gray'} onClick={() => onClose()}>
            {t('cancel')}
          </Button>
          <Button onClick={() => (value === 'parameter' ? onClickParameter() : onClickNote())}>{t('continue')}</Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}
